import { useState } from 'react';
import { Check, Loader2, Search } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { useTenantSentCampaigns } from '@/hooks/use-campaigns';
import { countAdvancedSalesFilters } from '@/lib/sales-history';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import type { SalesHistoryFilters } from '@/components/sales-history/use-sales-history-filters';

interface SalesFiltersSheetProps {
  filters: SalesHistoryFilters;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const rowClassName =
  'flex min-h-touch w-full items-center gap-3 rounded-lg px-3 text-left text-sm outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring';

function ToggleRow({
  label,
  pressed,
  onToggle,
}: {
  label: string;
  pressed: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={rowClassName}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <Check
        aria-hidden
        className={cn(
          'size-4 shrink-0 text-primary',
          pressed ? 'opacity-100' : 'opacity-0'
        )}
      />
    </button>
  );
}

/**
 * Mobile "Filtros avançados" (SALES-07 AC 2): paid/unpaid and campaigns as
 * lists inside the sheet (no Popover over the Drawer). The period stays in
 * the chips of the list. Changes apply right away; "Aplicar" closes.
 */
export function SalesFiltersSheet({
  filters,
  open,
  onOpenChange,
}: SalesFiltersSheetProps) {
  const { LL } = useI18nContext();
  const [campaignSearch, setCampaignSearch] = useState('');
  const { data: campaigns, isLoading: isCampaignsLoading } =
    useTenantSentCampaigns();

  const situations = [
    { value: 'paid', label: LL.salesPage.filters.paid() },
    { value: 'unpaid', label: LL.salesPage.filters.unpaid() },
  ];

  const search = campaignSearch.trim().toLocaleLowerCase();
  const visibleCampaigns = (campaigns ?? []).filter((c) =>
    c.name.toLocaleLowerCase().includes(search)
  );

  const toggleStatus = (sit: { value: string; label: string }) =>
    filters.setPaymentStatusFilter((prev) =>
      prev.some((s) => s.value === sit.value)
        ? prev.filter((s) => s.value !== sit.value)
        : [...prev, sit]
    );

  const toggleCampaign = (campaign: { id: string; name: string }) =>
    filters.setCampaignsFilter((prev) =>
      prev.some((c) => c.id === campaign.id)
        ? prev.filter((c) => c.id !== campaign.id)
        : [...prev, { id: campaign.id, name: campaign.name }]
    );

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>{LL.salesPage.filters.advancedFilters()}</DrawerTitle>
          <DrawerDescription>
            {LL.salesPage.mobile.filtersCount({
              count: countAdvancedSalesFilters(filters.values),
            })}
          </DrawerDescription>
        </DrawerHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-4">
          <section className="flex flex-col gap-1">
            <h3 className="px-3 text-xs font-medium text-muted-foreground">
              {LL.salesPage.filters.situation()}
            </h3>
            {situations.map((sit) => (
              <ToggleRow
                key={sit.value}
                label={sit.label}
                pressed={filters.paymentStatusFilter.some(
                  (s) => s.value === sit.value
                )}
                onToggle={() => toggleStatus(sit)}
              />
            ))}
          </section>

          <section className="flex flex-col gap-1">
            <h3 className="px-3 text-xs font-medium text-muted-foreground">
              {LL.salesPage.filters.campaigns()}
            </h3>
            <div className="relative mb-1">
              <Search
                aria-hidden
                className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
              <Input
                value={campaignSearch}
                onChange={(event) => setCampaignSearch(event.target.value)}
                placeholder={LL.salesPage.filters.searchCampaign()}
                className={cn('pl-8', mobileLayer.input)}
              />
            </div>
            {isCampaignsLoading ? (
              <Loader2 className="mx-auto my-3 size-4 animate-spin text-muted-foreground" />
            ) : visibleCampaigns.length === 0 ? (
              <p className="px-3 py-3 text-sm text-muted-foreground">
                {LL.salesPage.filters.noCampaign()}
              </p>
            ) : (
              visibleCampaigns.map((campaign) => (
                <ToggleRow
                  key={campaign.id}
                  label={campaign.name}
                  pressed={filters.campaignsFilter.some(
                    (c) => c.id === campaign.id
                  )}
                  onToggle={() => toggleCampaign(campaign)}
                />
              ))
            )}
          </section>
        </div>

        <DrawerFooter className="flex-row border-t">
          <Button
            type="button"
            variant="outline"
            className={cn('flex-1', mobileLayer.touch)}
            onClick={filters.clearFilters}
          >
            {LL.salesPage.mobile.clearFilters()}
          </Button>
          <Button
            type="button"
            className={cn('flex-1', mobileLayer.touch)}
            onClick={() => onOpenChange(false)}
          >
            {LL.salesPage.mobile.apply()}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
